<template>
  <div>
    <v-card class="mt-4">
      <v-toolbar color="primary h5 text-white">
        <div class="w-100 d-flex align-items-center justify-space-between">
          <div>
            {{ $t(itemprop.title) }}
          </div>
          <v-spacer></v-spacer>
        </div>
      </v-toolbar>

      <v-card-text
        class="pa-4 locked-card-wrapper position-relative"
        :style="{ minHeight: isLocked ? '350px' : 'auto' }"
      >
        <v-data-table
          disable-sort
          :headers="itemprop.data.header"
          :items="getItems"
          :options.sync="options"
          :server-items-length="total"
          :loading="loading"
          class="elevation-0 table-compact"
          :footer-props="{ 'items-per-page-options': item_per_page_list }"
        >
          <template
            v-for="(head, i) in itemprop.data.header"
            v-slot:[`item.`+head.value]="{ item, header, value }"
          >
            <ComponentSelector
              v-bind:key="i"
              :item="item"
              :header="header"
              :value="value"
            ></ComponentSelector>
          </template>
        </v-data-table>

        <!-- Blurred Overlay -->
        <transition name="fade-overlay">
          <div v-if="isLocked" class="locked-overlay">
            <div class="locked-overlay__backdrop"></div>
            <div class="locked-overlay__content">
              <div class="locked-overlay__icon-ring">
                <v-icon size="36" color="white">mdi-lock-outline</v-icon>
              </div>
              <h3 class="locked-overlay__title">
                {{ $t("plus_feature_overlay_title") }}
              </h3>
              <p
                class="locked-overlay__description"
                v-html="$t('plus_feature_overlay_description')"
              ></p>
              <v-btn
                color="#6C63FF"
                dark
                depressed
                large
                class="locked-overlay__cta"
                @click="handleSubscribeClick"
              >
                <v-icon left small>mdi-star-four-points</v-icon>
                {{ $t("subscribe_to_unlock") }}
              </v-btn>
            </div>
          </div>
        </transition>
      </v-card-text>
    </v-card>

    <SubscriptionPopup
      v-if="isLocked"
      ref="subscriptionPopup"
      @showSuccessModal="showSuccessModal"
    />
    <SuccessModal ref="successModal" />
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import SubscriptionPopup from "@/own/components/IqfPlus/SubscriptionPopup.vue";
import SuccessModal from "@/own/components/IqfPlus/SuccessModal.vue";
import Swal from "sweetalert2";
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
// import { SET_POPUP_CLOSED_FLAG } from "@/core/services/store/main.module";
import ComponentSelector from "@/own/components/datatable/ComponentSelector.vue";

export default {
  name: "PopUpDataTableObject",
  components: { ComponentSelector, SuccessModal, SubscriptionPopup },
  props: ["itemprop", "updateDetailsData", "id"],
  data: () => ({
    debounce: null,
    search: "",
    total: 0,
    options: {},
    loading: true,
    items: [],
    item_per_page_list: [20, 30, 40, 50],
  }),
  computed: {
    getItems() {
      return this.items;
    },
    isLocked() {
      return (
        Boolean(this.itemprop?.is_plus_feature) && !this.itemprop?.is_subscribed
      );
      // return true;
    },
  },
  methods: {
    showSuccessModal() {
      this.$refs.successModal.toggleModal();
      this.updateData();
    },
    handleSubscribeClick() {
      this.$refs.subscriptionPopup.toggleModal();
    },
    updateData() {
      this.updateDetailsData(true, false);
    },
    handleActionClick(menuItem) {
      if (
        menuItem.title.toLowerCase().includes("cancel") ||
        menuItem.title.toLowerCase().includes("delete") ||
        menuItem.title.toLowerCase().includes("remove")
      ) {
        Swal.fire({
          title: this.$t("are_you_sure"),
          text: this.$t("cannot_revert"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: this.$t("yes"),
        }).then((result) => {
          if (result.isConfirmed) {
            this.$store.commit(SET_PAGE_LOADING, true);
            ApiService.post(menuItem.url, { id: menuItem.id })
              .then(() => {
                this.updateData();
                Swal.fire(
                  this.$t("success"),
                  this.$t("request_submitted_successfully"),
                  "success"
                );
              })
              .finally(() => {
                this.$store.commit(SET_PAGE_LOADING, false);
              });
          }
        });
      }
    },

    getDataFromApi() {
      this.loading = true;
      const { page, itemsPerPage } = this.options;

      let data = {
        id: this.id,
        asn_id: this.itemprop?.asn_id,
        page: page,
        per_page: itemsPerPage,
      };

      if (this.search) {
        data.q = this.search;
      }

      ApiService.post(this.itemprop.url, data)
        .then((response) => {
          this.total = response.data.data.total;
          this.items = response.data.data.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    options: {
      handler() {
        this.getDataFromApi();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.locked-card-wrapper {
  position: relative;
}

/* ── Overlay layer ── */
.locked-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  overflow: hidden;
}

.locked-overlay__backdrop {
  position: absolute;
  inset: 0;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  background: rgba(255, 255, 255, 0.35);
}

/* ── Centered content block ── */
.locked-overlay__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 380px;
  padding: 36px 32px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(108, 99, 255, 0.08);
  border: 1px solid rgba(108, 99, 255, 0.12);
}

/* ── Lock icon ring ── */
.locked-overlay__icon-ring {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6c63ff 0%, #8b83ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-shadow: 0 4px 16px rgba(108, 99, 255, 0.3);
}

/* ── Typography ── */
.locked-overlay__title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px 0;
  letter-spacing: -0.01em;
}

.locked-overlay__description {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #5a5a7a;
  margin: 0 0 24px 0;
}

.locked-overlay__description strong {
  color: #6c63ff;
  font-weight: 600;
}

/* ── CTA button ── */
.locked-overlay__cta {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  letter-spacing: 0.02em !important;
  border-radius: 10px !important;
  padding: 0 28px !important;
  box-shadow: 0 4px 14px rgba(108, 99, 255, 0.35) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.locked-overlay__cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(108, 99, 255, 0.45) !important;
}

/* ── Fade transition ── */
.fade-overlay-enter-active,
.fade-overlay-leave-active {
  transition: opacity 0.3s ease;
}

.fade-overlay-enter,
.fade-overlay-leave-to {
  opacity: 0;
}

/* ── Responsive adjustments ── */
@media (max-width: 600px) {
  .locked-overlay__content {
    max-width: 90%;
    padding: 28px 20px;
  }

  .locked-overlay__title {
    font-size: 1.1rem;
  }

  .locked-overlay__description {
    font-size: 0.8125rem;
  }

  .locked-overlay__icon-ring {
    width: 52px;
    height: 52px;
  }

  .locked-overlay__icon-ring .v-icon {
    font-size: 28px !important;
  }
}
</style>
